interface EnvironmentConfig {
  apiSecret: string | null;
  isDevelopment: boolean;
  isProduction: boolean;
  aiEndpoint: string;
  // Removed hasOpenAI, hasAnthropic, hasGroq - users provide their own keys
}

// Build info injected at build time by Vite
export const BUILD_INFO = {
  version: __APP_VERSION__,
  buildTime: __BUILD_TIME__,
  commitHash: __COMMIT_HASH__,
};

class Environment {
  private config: EnvironmentConfig;

  constructor() {
    this.config = {
      apiSecret: this.getAPISecret(),
      isDevelopment: import.meta.env.DEV,
      isProduction: import.meta.env.PROD,
      aiEndpoint: import.meta.env.VITE_AI_ENDPOINT || '/api/ai'
      // Removed provider checks - users provide their own API keys
    };

    if (this.config.isDevelopment) {
      this.logConfiguration();
    }
  }

  private getAPISecret(): string | null {
    // In production, we use origin-based authentication, so no secret needed
    if (import.meta.env.PROD) {
      return 'production-origin-auth'; // Placeholder to indicate production auth mode
    }
    
    // In development, try multiple sources for the API secret
    return (
      // Vite environment variable
      import.meta.env.VITE_GODMODE_API_SECRET ||
      // Runtime global (set by server)
      (typeof window !== 'undefined' && (window as any).__GODMODE_API_SECRET__) ||
      // Development fallback
      this.generateDevSecret()
    );
  }

  private generateDevSecret(): string {
    // In development, create a consistent secret based on the environment
    const devSecret = 'dev-godmode-secret-' + btoa('localhost').replace(/=/g, '');
    console.log('🔧 Using development API secret:', devSecret.substring(0, 20) + '...');
    return devSecret;
  }

  private logConfiguration(): void {
    // console.log('🌍 Environment Configuration:', {
    //   isDevelopment: this.config.isDevelopment,
    //   isProduction: this.config.isProduction,
    //   hasApiSecret: !!this.config.apiSecret,
    //   aiEndpoint: this.config.aiEndpoint
    //   // AI providers are now configured by users, not environment
    // });

    if (!this.config.apiSecret && this.config.isDevelopment) {
      console.warn('⚠️ No API secret found. AI features may not work.');
      console.log('💡 For local development, set VITE_GODMODE_API_SECRET in .env.local');
    }
  }

  get apiSecret() { return this.config.apiSecret; }
  get isDevelopment() { return this.config.isDevelopment; }
  get isProduction() { return this.config.isProduction; }
  get aiEndpoint() { return this.config.aiEndpoint; }

  isConfigured(): boolean {
    // Environment is considered configured if we have the basic API endpoint
    // Users provide their own AI provider keys
    return !!this.config.aiEndpoint;
  }

  getConfigurationStatus(): {
    configured: boolean;
    hasApiSecret: boolean;
    missingRequirements: string[];
  } {
    const missingRequirements: string[] = [];

    if (!this.config.apiSecret && this.config.isDevelopment) {
      missingRequirements.push('API secret (VITE_GODMODE_API_SECRET) - dev only');
    }

    return {
      configured: this.isConfigured(),
      hasApiSecret: !!this.config.apiSecret,
      missingRequirements
    };
  }
}

export const environment = new Environment();

// Expose in development for debugging
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as any).environment = environment;
  console.log('🔧 Environment utilities available: window.environment');
}